import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { auth } from '../config/firebase';
import {
  PerformanceMetrics,
  PerformanceHistory,
  StrategyPerformance,
  Trade,
  Position,
  Bar,
} from '../types';
import {
  Strategy,
  StrategyStatus,
  StrategyMode,
  StrategyType,
  MarketplaceStatus,
} from '../types/strategy';
import {
  User,
  PasskeyChallenge,
  PasskeyCredential,
  PasskeyAuthenticationChallenge
} from '../types/auth';

class FirestoreService {
  private static instance: FirestoreService;

  private constructor() {}

  public static getInstance(): FirestoreService {
    if (!FirestoreService.instance) {
      FirestoreService.instance = new FirestoreService();
    }
    return FirestoreService.instance;
  }

  // User Methods
  async getOrCreateUser(firebaseUser: any): Promise<User> {
    const userRef = doc(db, 'users', firebaseUser.uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data() as User;
    }

    const userData: User = {
      id: firebaseUser.uid,
      phoneNumber: firebaseUser.phoneNumber || '',
      email: firebaseUser.email || '',
      name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Anonymous',
      photoURL: firebaseUser.photoURL || '',
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
      provider: 'google',
      authMethods: {
        google: {
          uid: firebaseUser.uid,
          createdAt: new Date().toISOString(),
          lastVerifiedAt: new Date().toISOString()
        }
      },
      preferences: {
        theme: 'system',
        notifications: {
          sms: Boolean(firebaseUser.phoneNumber),
          push: true,
          trades: true,
          performance: true,
        },
      },
    };

    await setDoc(userRef, userData);
    return userData;
  }

  async getUser(userId: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data() as User;
      }
      return null;
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  async getUserByPhone(phoneNumber: string): Promise<User | null> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('phoneNumber', '==', phoneNumber));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data() as User;
      }
      return null;
    } catch (error) {
      console.error('Error getting user by phone:', error);
      throw error;
    }
  }

  async createUser(userId: string, userData: Partial<User>): Promise<User> {
    try {
      const now = new Date().toISOString();
      const newUser: User = {
        id: userId,
        phoneNumber: userData.phoneNumber || '',
        email: userData.email || '',
        name: userData.name || '',
        photoURL: userData.photoURL || '',
        createdAt: now,
        lastLoginAt: now,
        provider: userData.provider || 'google',
        authMethods: userData.authMethods || {
          google: {
            uid: userId,
            createdAt: now,
            lastVerifiedAt: now
          }
        },
        preferences: {
          theme: 'system',
          notifications: {
            sms: false,
            push: false,
            trades: false,
            performance: false
          }
        }
      };

      await setDoc(doc(db, 'users', userId), newUser);
      return newUser;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { ...updates, lastLoginAt: new Date().toISOString() });
      const updatedDoc = await getDoc(userRef);
      return updatedDoc.data() as User;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async saveUser(user: User): Promise<void> {
    await setDoc(doc(db, 'users', user.id), user);
  }

  // Strategy Methods
  async getAllStrategies(userId: string): Promise<Strategy[]> {
    try {
      const strategiesRef = collection(db, 'strategies');
      const q = query(
        strategiesRef,
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Strategy[];
    } catch (error) {
      console.error('Error getting strategies:', error);
      throw error;
    }
  }

  async getStrategy(strategyId: string): Promise<Strategy | null> {
    try {
      const strategyRef = doc(db, 'strategies', strategyId);
      const strategyDoc = await getDoc(strategyRef);
      if (!strategyDoc.exists()) {
        return null;
      }
      return {
        id: strategyDoc.id,
        ...strategyDoc.data()
      } as Strategy;
    } catch (error) {
      console.error('Error getting strategy:', error);
      throw error;
    }
  }

  async saveStrategy(strategy: Partial<Strategy>, userId: string): Promise<string> {
    try {
      // Base strategy structure with defaults
      const baseStrategy: Omit<Strategy, 'id'> = {
        userId,
        name: strategy.name || 'New Strategy',
        description: strategy.description || '',
        mode: strategy.mode || StrategyMode.EXPERIMENTAL,
        type: strategy.type || StrategyType.CUSTOM,
        status: strategy.status || StrategyStatus.DRAFT,
        code: strategy.code || '',
        parameters: strategy.parameters || {
          timeframe: '1d',
          universe: ['AAPL', 'GOOGL', 'MSFT'],
          exchange: 'alpaca',
          risk_management: {
            max_position_size: 0.3,
            stop_loss: 0.02,
          },
        },
        metadata: strategy.metadata || {},
        tags: strategy.tags || [],
        marketplaceStatus: strategy.marketplaceStatus || 'PRIVATE',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Helper function to clean undefined values
      const cleanObject = (obj: Record<string, any>): Record<string, any> => {
        const result: Record<string, any> = {};
        Object.entries(obj).forEach(([key, value]) => {
          result[key] = value === undefined ? null : value;
        });
        return result;
      };

      if (!strategy.id) {
        // Create new strategy with auto-generated ID
        const docRef = await addDoc(collection(db, 'strategies'), cleanObject(baseStrategy));
        return docRef.id;
      } else {
        // Check if document exists
        const strategyRef = doc(db, 'strategies', strategy.id);
        const strategyDoc = await getDoc(strategyRef);
        
        const mergedStrategy = {
          ...(strategyDoc.exists() ? strategyDoc.data() : baseStrategy),
          ...strategy,
          updatedAt: new Date().toISOString(),
        };
        
        // Clean strategy object (convert undefined to null)
        const cleanStrategy = cleanObject(mergedStrategy);
        
        // Use setDoc instead of updateDoc to handle both cases
        await setDoc(strategyRef, cleanStrategy);
        return strategy.id;
      }
    } catch (error) {
      console.error('Error saving strategy:', error);
      throw error;
    }
  }

  async deleteStrategy(strategyId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'strategies', strategyId));
    } catch (error) {
      console.error('Error deleting strategy:', error);
      throw error;
    }
  }

  async publishStrategy(strategyId: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const strategyRef = doc(db, 'strategies', strategyId);
      const strategyDoc = await getDoc(strategyRef);
      
      if (!strategyDoc.exists()) {
        throw new Error('Strategy not found');
      }
      
      const strategyData = strategyDoc.data() as Strategy;
      
      // Check if the strategy belongs to the current user
      if (strategyData.userId !== user.uid) {
        throw new Error('You do not have permission to publish this strategy');
      }
      
      // Update the strategy with marketplace status and author information
      await updateDoc(strategyRef, {
        marketplaceStatus: MarketplaceStatus.PUBLISHED,
        authorName: user.displayName || user.email?.split('@')[0] || 'User',
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error publishing strategy:', error);
      throw error;
    }
  }

  async getStrategies(userId: string): Promise<Strategy[]> {
    const strategiesQuery = query(collection(db, 'strategies'), where('userId', '==', userId));
    const snapshot = await getDocs(strategiesQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Strategy));
  }

  async updateStrategy(id: string, updates: Partial<Strategy>): Promise<void> {
    const strategyRef = doc(db, 'strategies', id);
    await updateDoc(strategyRef, updates);
  }

  async deleteStrategyOld(id: string, userId: string): Promise<void> {
    try {
      const strategyRef = doc(db, 'strategies', id);
      const strategyDoc = await getDoc(strategyRef);
      
      if (!strategyDoc.exists()) {
        throw new Error('Strategy not found');
      }
      
      const strategy = strategyDoc.data();
      if (strategy.userId !== userId) {
        throw new Error('Unauthorized to delete this strategy');
      }
      
      await deleteDoc(strategyRef);
    } catch (error) {
      console.error('Error deleting strategy:', error);
      throw error;
    }
  }

  async getMarketplaceStrategies(): Promise<Strategy[]> {
    try {
      const strategiesRef = collection(db, 'strategies');
      const q = query(
        strategiesRef, 
        where('marketplaceStatus', 'in', ['PUBLISHED', 'FEATURED'])
      );
      
      const querySnapshot = await getDocs(q);
      const strategies: Strategy[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data() as Strategy;
        strategies.push({
          ...data,
          id: doc.id
        });
      });
      
      return strategies;
    } catch (error) {
      console.error('Error getting marketplace strategies:', error);
      return [];
    }
  }

  // Trade Methods
  async saveTrade(trade: Trade): Promise<void> {
    await addDoc(collection(db, 'trades'), trade);
  }

  async getTradesByStrategy(strategyId: string): Promise<Trade[]> {
    const tradesRef = collection(db, 'trades');
    const q = query(
      tradesRef,
      where('strategy', '==', strategyId),
      orderBy('executed_at', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      executed_at: (doc.data().executed_at as Timestamp).toDate().toISOString(),
    }) as Trade);
  }

  async getTrades(userId: string): Promise<Trade[]> {
    const tradesQuery = query(collection(db, 'trades'), where('userId', '==', userId));
    const snapshot = await getDocs(tradesQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Trade));
  }

  // Performance Methods
  async saveStrategyPerformance(performance: StrategyPerformance): Promise<void> {
    const perfRef = doc(db, 'performances', performance.strategyName);
    await setDoc(perfRef, {
      ...performance,
      lastUpdated: performance.lastUpdated,
      metrics: performance.metrics ? {
        ...performance.metrics,
        lastUpdated: performance.metrics.lastUpdated
      } : undefined
    });
  }

  async getStrategyPerformance(strategyName: string): Promise<StrategyPerformance | null> {
    const perfRef = doc(db, 'performance', strategyName);
    const perfDoc = await getDoc(perfRef);
    return perfDoc.exists() ? {
      ...perfDoc.data(),
      lastUpdated: perfDoc.data().lastUpdated,
    } as StrategyPerformance : null;
  }

  async getPerformance(strategyId: string): Promise<StrategyPerformance | null> {
    const perfRef = doc(db, 'performances', strategyId);
    const perfDoc = await getDoc(perfRef);
    if (!perfDoc.exists()) return null;

    const data = perfDoc.data();
    return {
      ...data,
      lastUpdated: data.lastUpdated,
      metrics: data.metrics ? {
        ...data.metrics,
        lastUpdated: data.metrics.lastUpdated
      } : undefined
    } as StrategyPerformance;
  }

  async savePerformanceHistory(performance: PerformanceHistory): Promise<void> {
    await addDoc(collection(db, 'performance'), performance);
  }

  async getPerformanceHistory(
    strategyId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<PerformanceHistory[]> {
    if (startDate && endDate) {
      const historyRef = collection(db, 'performanceHistory');
      const q = query(
        historyRef,
        where('strategyId', '==', strategyId),
        where('timestamp', '>=', startDate.toISOString()),
        where('timestamp', '<=', endDate.toISOString()),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        const metrics: PerformanceMetrics = {
          totalReturn: Number(data.metrics?.totalReturn) || 0,
          maxDrawdown: Number(data.metrics?.maxDrawdown) || 0,
          sharpeRatio: Number(data.metrics?.sharpeRatio) || 0,
          dailyReturns: Array.isArray(data.metrics?.dailyReturns) ? data.metrics.dailyReturns.map(Number) : [],
          winRate: Number(data.metrics?.winRate) || 0,
          profitFactor: Number(data.metrics?.profitFactor) || 0,
          averageWin: Number(data.metrics?.averageWin) || 0,
          averageLoss: Number(data.metrics?.averageLoss) || 0,
          lastUpdated: data.metrics?.lastUpdated || new Date().toISOString()
        };

        return {
          id: doc.id,
          strategyId: String(data.strategyId || strategyId),
          timestamp: String(data.timestamp || new Date().toISOString()),
          metrics
        } as PerformanceHistory;
      });
    } else {
      const performanceQuery = query(
        collection(db, 'performance'),
        where('strategyId', '==', strategyId)
      );
      const snapshot = await getDocs(performanceQuery);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        const metrics: PerformanceMetrics = {
          totalReturn: Number(data.metrics?.totalReturn) || 0,
          maxDrawdown: Number(data.metrics?.maxDrawdown) || 0,
          sharpeRatio: Number(data.metrics?.sharpeRatio) || 0,
          dailyReturns: Array.isArray(data.metrics?.dailyReturns) ? data.metrics.dailyReturns.map(Number) : [],
          winRate: Number(data.metrics?.winRate) || 0,
          profitFactor: Number(data.metrics?.profitFactor) || 0,
          averageWin: Number(data.metrics?.averageWin) || 0,
          averageLoss: Number(data.metrics?.averageLoss) || 0,
          lastUpdated: data.metrics?.lastUpdated || new Date().toISOString()
        };

        return {
          id: doc.id,
          strategyId: String(data.strategyId || strategyId),
          timestamp: String(data.timestamp || new Date().toISOString()),
          metrics
        } as PerformanceHistory;
      });
    }
  }

  async getLatestPerformanceHistory(strategyId: string): Promise<PerformanceHistory[]> {
    const historyRef = collection(db, 'performanceHistory');
    const q = query(
      historyRef,
      where('strategyId', '==', strategyId),
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      timestamp: doc.data().timestamp,
      metrics: {
        ...doc.data().metrics,
        lastUpdated: doc.data().metrics.lastUpdated
      }
    })) as PerformanceHistory[];
  }

  async createPasskeyChallenge(userId: string): Promise<PasskeyChallenge> {
    const challengeId = crypto.randomUUID();
    const challengeRef = doc(db, 'passkey_challenges', challengeId);
    
    // Create base challenge data
    const challengeData: PasskeyChallenge = {
      challengeId,
      challenge: crypto.randomUUID(),
      userId,
      type: 'registration',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(), // 5 minutes
      used: false,
      rp: {
        name: 'Strategiz',
        id: window.location.hostname,
      },
      user: {
        id: userId,
        name: 'User'  // Default value
      },
      pubKeyCredParams: [
        {
          alg: -7, // ES256
          type: 'public-key',
        },
        {
          alg: -257, // RS256
          type: 'public-key',
        },
      ],
      timeout: 60000,
      attestation: 'direct',
      authenticatorSelection: {
        residentKey: 'required',
        userVerification: 'required',
        authenticatorAttachment: 'platform',
      },
      excludeCredentials: [], // We'll update this with existing credentials
    };

    // Get user data to update the challenge
    const user = await this.getUser(userId);
    if (user?.email) {  // Only update if we have email
      challengeData.user.name = user.email.split('@')[0];
    }

    // Get existing credentials to prevent re-registration
    const existingPasskeys = await this.getPasskeys(userId);
    challengeData.excludeCredentials = existingPasskeys.map(passkey => ({
      id: passkey.credentialId,
      type: 'public-key',
      transports: ['internal'],
    }));

    await setDoc(challengeRef, challengeData);
    return challengeData;
  }

  async verifyPasskeyRegistration(
    userId: string,
    challengeId: string,
    registrationResponse: any
  ): Promise<void> {
    const challengeRef = doc(db, 'passkeyChallenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    const challenge = challengeSnap.data() as PasskeyChallenge;
    if (challenge.used || new Date(challenge.expiresAt) < new Date()) {
      throw new Error('Challenge expired or already used');
    }

    // Create new passkey credential
    const credentialRef = doc(collection(db, 'passkeys'));
    const credentialData: PasskeyCredential = {
      id: credentialRef.id,
      userId,
      credentialId: registrationResponse.id,
      publicKey: registrationResponse.response.publicKey,
      counter: 0,
      createdAt: new Date().toISOString(),
      lastUsedAt: new Date().toISOString(),
    };

    // Update challenge as used
    await updateDoc(challengeRef, { used: true });
    // Save credential
    await setDoc(credentialRef, credentialData);
  }

  async getPasskeyAuthenticationChallenge(phoneNumber: string): Promise<PasskeyAuthenticationChallenge> {
    // Get user by phone number
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('phoneNumber', '==', phoneNumber), limit(1));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      throw new Error('User not found');
    }

    const userId = snapshot.docs[0].id;
    const challenge = crypto.randomUUID();
    const challengeRef = doc(collection(db, 'passkeyChallenges'));

    // Get user's registered passkeys
    const passkeys = await this.getPasskeys(userId);
    if (passkeys.length === 0) {
      throw new Error('No passkeys registered for this user');
    }

    const challengeData: PasskeyAuthenticationChallenge = {
      challengeId: challengeRef.id,
      challenge,
      userId,
      type: 'authentication',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(), // 5 minutes
      used: false,
      allowCredentials: passkeys.map(passkey => ({
        id: passkey.credentialId,
        type: 'public-key',
        transports: ['internal'],
      })),
      timeout: 60000,
      userVerification: 'required',
      rpId: window.location.hostname,
    };

    await setDoc(challengeRef, challengeData);
    return challengeData;
  }

  async verifyPasskeyAuthentication(
    phoneNumber: string,
    challengeId: string,
    authenticationResponse: any
  ): Promise<User> {
    // Verify challenge
    const challengeRef = doc(db, 'passkeyChallenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    const challenge = challengeSnap.data() as PasskeyChallenge;
    if (challenge.used || new Date(challenge.expiresAt) < new Date()) {
      throw new Error('Challenge expired or already used');
    }

    // Get credential
    const passkeysRef = collection(db, 'passkeys');
    const q = query(passkeysRef, where('credentialId', '==', authenticationResponse.id));
    const credentialSnap = await getDocs(q);

    if (credentialSnap.empty) {
      throw new Error('Credential not found');
    }

    const credential = credentialSnap.docs[0].data() as PasskeyCredential;

    // Verify signature using credential's public key
    // Note: In a production environment, you would verify the signature here
    // For this implementation, we'll skip the cryptographic verification

    // Update credential counter and last used time
    await updateDoc(credentialSnap.docs[0].ref, {
      counter: credential.counter + 1,
      lastUsedAt: new Date().toISOString(),
    });

    // Mark challenge as used
    await updateDoc(challengeRef, { used: true });

    // Get and return user
    const userRef = doc(db, 'users', credential.userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error('User not found');
    }

    return userSnap.data() as User;
  }

  async getPasskeys(userId: string): Promise<PasskeyCredential[]> {
    const passkeysRef = collection(db, 'passkeys');
    const q = query(passkeysRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as PasskeyCredential);
  }

  async removePasskey(userId: string, credentialId: string): Promise<void> {
    const passkeysRef = collection(db, 'passkeys');
    const q = query(
      passkeysRef,
      where('userId', '==', userId),
      where('credentialId', '==', credentialId)
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      await deleteDoc(snapshot.docs[0].ref);
    }
  }

  // Get exchange credentials for a user
  async getExchangeCredentials(email: string, exchange: string) {
    if (!email) return null;
    
    try {
      // Get the user document
      const userDoc = await getDoc(doc(db, 'users', email));
      
      if (!userDoc.exists()) return null;
      
      // Get the api_credentials subcollection
      const credentialDoc = await getDoc(
        doc(db, 'users', email, 'api_credentials', exchange)
      );
      
      if (!credentialDoc.exists()) return null;
      
      return credentialDoc.data();
    } catch (error) {
      console.error('Error getting exchange credentials:', error);
      return null;
    }
  }

  // Save exchange credentials for a user
  async saveExchangeCredentials(
    email: string, 
    exchange: string, 
    credentials: { apiKey: string, secretKey: string }
  ) {
    if (!email) throw new Error('No user email provided');
    
    try {
      // Save to the api_credentials subcollection
      await setDoc(
        doc(db, 'users', email, 'api_credentials', exchange),
        {
          apiKey: credentials.apiKey,
          secretKey: credentials.secretKey,
          createdAt: Timestamp.now()
        }
      );
      
      return true;
    } catch (error) {
      console.error('Error saving exchange credentials:', error);
      throw error;
    }
  }
}

export const firestoreService = FirestoreService.getInstance();
